import { funcaoDe, type Funcao } from './posicoes';
import type { Jogador, Posicao } from './tipos';

/**
 * Habilidades individuais (base própria, inspirada nos atributos que o Brasfoot mostra: Gol, Des, Arm, Fin, Vel, Tec, Pas).
 * Não há fonte real para esses números: eles saem do overall do jogador, da posição e das características dele, com uma
 * pequena variação fixa por jogador (sempre os mesmos valores para o mesmo jogador). Com o modo "habilidade individual"
 * ligado, a força do jogador na partida passa a ser a média ponderada das habilidades que importam para a posição dele.
 */

export interface Habilidades {
  gol: number;
  des: number;
  arm: number;
  fin: number;
  vel: number;
  tec: number;
  pas: number;
}

export type ChaveHabilidade = keyof Habilidades;

export const NOMES_HABILIDADE: Record<ChaveHabilidade, string> = {
  gol: 'Goleiro',
  des: 'Desarme',
  arm: 'Armação',
  fin: 'Finalização',
  vel: 'Velocidade',
  tec: 'Técnica',
  pas: 'Passe',
};

const CHAVES: ChaveHabilidade[] = ['gol', 'des', 'arm', 'fin', 'vel', 'tec', 'pas'];

/** Perfil de cada posição: quanto cada habilidade fica acima ou abaixo do overall. */
const PERFIL: Record<Posicao, Habilidades> = {
  G: { gol: 14, des: -18, arm: -22, fin: -28, vel: -14, tec: -12, pas: -8 },
  Z: { gol: -30, des: 12, arm: -8, fin: -14, vel: -2, tec: -6, pas: -2 },
  L: { gol: -30, des: 2, arm: -2, fin: -8, vel: 10, tec: 0, pas: 4 },
  M: { gol: -30, des: -2, arm: 9, fin: -4, vel: 0, tec: 4, pas: 9 },
  A: { gol: -30, des: -16, arm: -2, fin: 12, vel: 8, tec: 6, pas: -2 },
};

/** Características (índices de CARACTERISTICAS) que elevam uma habilidade. */
const BONUS_CARACTERISTICA: Partial<Record<number, [ChaveHabilidade, number]>> = {
  0: ['gol', 5], // Col
  1: ['gol', 4], // DPe
  2: ['gol', 5], // Ref
  3: ['gol', 4], // SGo
  4: ['arm', 8], // Arm
  5: ['fin', 3], // Cab
  6: ['pas', 4], // Cru
  7: ['des', 8], // Des
  8: ['tec', 8], // Dri
  9: ['fin', 9], // Fin
  10: ['des', 5], // Mar
  11: ['pas', 8], // Pas
  13: ['vel', 9], // Vel
};

/** Número 0..1 fixo para (jogador, habilidade). */
function ruido(id: number, k: number): number {
  let h = (id * 2654435761 + k * 40503) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519) >>> 0;
  h ^= h >>> 13;
  return (h % 1000) / 1000;
}

const limitar = (v: number): number => Math.max(1, Math.min(99, Math.round(v)));

/** Habilidades do jogador (calculadas uma vez e guardadas no próprio jogador). */
export function habilidadesDe(j: Jogador): Habilidades {
  if (j.habilidades) return j.habilidades;
  const out = {} as Habilidades;
  CHAVES.forEach((k, i) => {
    out[k] = j.overall + PERFIL[j.posicao][k] + (ruido(j.id, i) - 0.5) * 8;
  });
  for (const c of j.caracteristicas) {
    const b = BONUS_CARACTERISTICA[c];
    if (b) out[b[0]] += b[1];
  }
  for (const k of CHAVES) out[k] = limitar(out[k]);
  j.habilidades = out;
  return out;
}

/** Peso de cada habilidade na força de cada posição (soma 1). */
const PESOS: Record<Posicao, Partial<Record<ChaveHabilidade, number>>> = {
  G: { gol: 0.7, vel: 0.1, tec: 0.1, pas: 0.1 },
  Z: { des: 0.42, vel: 0.14, tec: 0.1, pas: 0.14, arm: 0.1, fin: 0.1 },
  L: { des: 0.28, vel: 0.24, pas: 0.18, tec: 0.14, arm: 0.1, fin: 0.06 },
  M: { arm: 0.28, pas: 0.28, tec: 0.18, des: 0.14, vel: 0.06, fin: 0.06 },
  A: { fin: 0.4, vel: 0.2, tec: 0.18, pas: 0.1, arm: 0.12 },
};

/** Pesos próprios das funções que se afastam do perfil médio da posição (quando o jogador joga na posição dele). */
const PESOS_FUNCAO: Partial<Record<Funcao, Partial<Record<ChaveHabilidade, number>>>> = {
  VOL: { des: 0.34, pas: 0.22, vel: 0.08, tec: 0.1, arm: 0.18, fin: 0.08 },
  MEI: { arm: 0.34, pas: 0.26, tec: 0.2, fin: 0.1, des: 0.04, vel: 0.06 },
  PE: { vel: 0.3, tec: 0.24, fin: 0.26, pas: 0.1, arm: 0.1 },
  PD: { vel: 0.3, tec: 0.24, fin: 0.26, pas: 0.1, arm: 0.1 },
  SA: { tec: 0.26, fin: 0.3, arm: 0.2, vel: 0.14, pas: 0.1 },
  CA: { fin: 0.5, vel: 0.14, tec: 0.16, pas: 0.06, arm: 0.14 },
};

/** Força de 0 a 100 pela média ponderada das habilidades que importam para a posição (ou para a função, se ele joga na dele). */
export function forcaPorHabilidade(j: Jogador, posicao: Posicao = j.posicao): number {
  const h = habilidadesDe(j);
  const pesos = (posicao === j.posicao ? PESOS_FUNCAO[funcaoDe(j)] : undefined) ?? PESOS[posicao];
  const total = TOTAL_PESOS.get(pesos) as number;
  let soma = 0;
  for (const k of CHAVES) soma += h[k] * (pesos[k] ?? 0);
  return total > 0 ? soma / total : j.overall;
}

/** Soma dos pesos de cada perfil, calculada uma vez (a função roda muitas vezes por partida). */
const TOTAL_PESOS = new Map<Partial<Record<ChaveHabilidade, number>>, number>();
for (const p of [...Object.values(PESOS), ...Object.values(PESOS_FUNCAO)] as Partial<Record<ChaveHabilidade, number>>[]) {
  TOTAL_PESOS.set(p, CHAVES.reduce((s, k) => s + (p[k] ?? 0), 0));
}

// Modo global usado pelo motor de partida (definido por simularRodada a partir do estado do jogo).
let modoHabilidade = true;

export function definirModoHabilidade(ligado: boolean): void {
  modoHabilidade = ligado;
}

export function modoHabilidadeLigado(): boolean {
  return modoHabilidade;
}
